import { useCallback, useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ChevronLeft, Image } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, FetchParams } from '@/components/DataTable/types'
import { fetchGroupMedia, type AdminMedia } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / 1024 / 1024).toFixed(1)} MB`
}

/**
 * A4.14 — Media galereya (moderatsiya). GET /admin/groups/:id/media (backend
 * `d148af0`). Metadata + blurhash (bayt-preview media-proxy user-token'ini kutadi,
 * follow-up). Kim yukladi → foydalanuvchi kartasiga havola.
 */
export function GroupMedia() {
  const t = useT()
  const { id = '' } = useParams()

  const columns = useMemo<AdminColumn<AdminMedia>[]>(
    () => [
      { key: 'seq', header: '#', type: 'text', width: 70 },
      { key: 'kind', header: t.col.type, type: 'text', width: 110 },
      { key: 'mime', header: t.groups.mime, type: 'text', width: 150 },
      {
        key: 'size_bytes',
        header: t.groups.size,
        type: 'number',
        width: 100,
        align: 'right',
        render: (r) => <span className="font-mono tabular">{fmtBytes(r.size_bytes)}</span>,
      },
      {
        key: 'width',
        header: t.groups.dimensions,
        type: 'text',
        width: 120,
        sortable: false,
        render: (r) => (r.width && r.height ? `${r.width}×${r.height}` : '—'),
      },
      {
        key: 'owner_id',
        header: t.groups.uploader,
        type: 'text',
        width: 150,
        sortable: false,
        render: (r) =>
          r.owner_id ? (
            <Link to={`/users/${r.owner_id}`} className="text-accent hover:underline">
              {r.username ? `@${r.username}` : `#${r.owner_id}`}
            </Link>
          ) : (
            <span className="text-tertiary">—</span>
          ),
      },
      { key: 'created_at', header: t.col.time, type: 'date', width: 160 },
    ],
    [t],
  )

  const fetcher = useCallback((params: FetchParams) => fetchGroupMedia(Number(id), params), [id])

  return (
    <>
      <PageHeader
        title={t.groups.media}
        screenId="A4.14"
        description={t.groups.mediaNote}
        actions={
          <Link
            to={`/groups/${id}`}
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.groups.cardTitle}
          </Link>
        }
      />
      <DataTable<AdminMedia>
        screenId="A4.14"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => r.file_id}
        exportable
        emptyState={{ icon: <Image size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
